<template>
  <div class="admin-notes-panel" dir="rtl">
    <!-- Header -->
    <div class="anp-header">
      <div class="anp-header__title">
        <i class="fa-solid fa-note-sticky me-2"></i>
        ملاحظات الإدارة
        <span v-if="countRows > 0" class="anp-badge">{{ countRows }}</span>
      </div>
    </div>

    <!-- Add note form -->
    <div v-if="checkAccess('admin_notes_create')" class="anp-compose">
      <div class="anp-compose__avatar">
        <img
          v-if="GET_USER_DATA?.avatar"
          :src="$store.getters.MEDIA_URL + GET_USER_DATA.avatar"
          :alt="GET_USER_DATA.userName"
        />
        <i v-else class="fa-solid fa-user-tie"></i>
      </div>
      <div class="anp-compose__field">
        <textarea
          v-model="newComment"
          class="anp-textarea"
          placeholder="اكتب ملاحظة للإدارة..."
          rows="2"
          @keydown.ctrl.enter.prevent="submitNote"
        ></textarea>
        <div class="anp-compose__actions">
          <span class="anp-hint">Ctrl + Enter للإضافة</span>
          <button
            class="anp-submit-btn"
            :disabled="submitLoading || !newComment.trim()"
            @click="submitNote"
          >
            <span v-if="!submitLoading"><i class="fa-solid fa-paper-plane me-1"></i> إضافة</span>
            <span v-else><i class="fa-solid fa-spinner fa-spin me-1"></i> جاري الإرسال...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Notes list -->
    <div class="anp-body">
      <div v-if="listLoading" class="anp-loader">
        <i class="fa-solid fa-spinner fa-spin fa-lg"></i>
      </div>

      <div v-else-if="!rows.length" class="anp-empty">
        <i class="fa-regular fa-note-sticky fa-2x mb-2 d-block"></i>
        لا توجد ملاحظات بعد
      </div>

      <template v-else>
        <transition-group name="note-list" tag="div">
          <div v-for="note in rows" :key="note.id" class="anp-note">
            <!-- Avatar -->
            <div class="anp-note__avatar">
              <img
                v-if="note.Admin && note.Admin.avatar"
                :src="$store.getters.MEDIA_URL + note.Admin.avatar"
                :alt="note.Admin.name"
              />
              <i v-else class="fa-solid fa-user-tie"></i>
            </div>

            <!-- Content -->
            <div class="anp-note__body">
              <div class="anp-note__meta">
                <span class="anp-note__author">
                  {{ note.Admin ? note.Admin.name : 'مجهول' }}
                </span>
                <span class="anp-note__time">{{ formatDate(note.createdAt) }}</span>
                <span v-if="note.updatedAt !== note.createdAt" class="anp-note__edited">(معدّل)</span>
              </div>

              <!-- View mode -->
              <div v-if="editingId !== note.id" class="anp-note__text">{{ note.comment }}</div>

              <!-- Edit mode -->
              <div v-else class="anp-note__edit">
                <textarea
                  v-model="editComment"
                  class="anp-textarea anp-textarea--sm"
                  rows="3"
                ></textarea>
                <div class="anp-note__edit-actions">
                  <button class="anp-btn anp-btn--save" :disabled="saveLoading" @click="saveEdit(note)">
                    <i class="fa-solid fa-check me-1"></i>حفظ
                  </button>
                  <button class="anp-btn anp-btn--cancel" @click="cancelEdit">إلغاء</button>
                </div>
              </div>

              <!-- Note actions (edit / delete) -->
              <div
                v-if="canManage(note) && editingId !== note.id"
                class="anp-note__actions"
              >
                <button
                  v-if="checkAccess('admin_notes_update')"
                  class="anp-action-btn"
                  @click="startEdit(note)"
                ><i class="bi bi-pencil"></i> تعديل</button>
                <button
                  v-if="checkAccess('admin_notes_delete')"
                  class="anp-action-btn anp-action-btn--danger"
                  @click="deleteNote(note)"
                ><i class="bi bi-trash"></i> حذف</button>
              </div>
            </div>
          </div>
        </transition-group>
      </template>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="anp-pagination">
      <button v-if="currentPage > 1" class="anp-page-btn" @click="changePage(currentPage - 1)">
        <i class="bi bi-chevron-right"></i>
      </button>
      <button
        v-for="p in totalPages" :key="p"
        class="anp-page-btn"
        :class="{ 'anp-page-btn--active': p === currentPage }"
        @click="changePage(p)"
      >{{ p }}</button>
      <button v-if="currentPage < totalPages" class="anp-page-btn" @click="changePage(currentPage + 1)">
        <i class="bi bi-chevron-left"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { toast, checkAccess, formatDate } from '@/helpers/tools';
import { mapGetters } from 'vuex';

export default {
  name: 'AdminNotesPanel',

  props: {
    modelName: {
      type: String,
      required: true,
    },
    recordId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      listLoading: true,
      submitLoading: false,
      saveLoading: false,
      rows: [],
      pages: [],
      countRows: 0,
      currentPage: 1,
      newComment: '',
      editingId: null,
      editComment: '',
    };
  },
  computed: {
    ...mapGetters(['GET_USER_DATA']),

    totalPages() {
      return Math.ceil(this.countRows / 5);
    },
  },
  methods: {
    checkAccess,
    formatDate,

    canManage(note) {
      const user = this.$store.getters.GET_USER_DATA;
      if (!user) return false;
      return user.isSuperUser || note.Admin?.id === user.id;
    },

    fetchNotes() {
      this.listLoading = true;
      this.$http
        .get('/api/admin/adminNotes/list', {
          params: {
            modelName: this.modelName,
            recordId: this.recordId,
            page: this.currentPage,
          },
        })
        .then(({ data }) => {
          this.rows = data.rows;
          this.countRows = data.countRows ?? 0;
          if (this.rows.length === 0 && this.currentPage > 1) {
            this.currentPage -= 1;
            this.fetchNotes();
          }
        })
        .catch((err) => {
          toast('تعذّر تحميل الملاحظات');
        })
        .finally(() => {
          this.listLoading = false;
        });
    },

    changePage(p) {
      this.currentPage = p;
      this.fetchNotes();
    },

    submitNote() {
      if (!this.newComment.trim()) return;
      this.submitLoading = true;
      this.$http
        .post('/api/admin/adminNotes/create', {
          modelName: this.modelName,
          recordId: this.recordId,
          comment: this.newComment,
        })
        .then(({ data }) => {
          this.newComment = '';
          this.countRows += 1;
          this.rows.unshift(data.row);
          if (this.rows.length > 5) this.rows.pop();
          toast('تمت إضافة الملاحظة', 'success');
        })
        .catch(() => {
          toast('تعذّر إضافة الملاحظة');
        })
        .finally(() => {
          this.submitLoading = false;
        });
    },

    startEdit(note) {
      this.editingId  = note.id;
      this.editComment = note.comment;
    },

    cancelEdit() {
      this.editingId  = null;
      this.editComment = '';
    },

    saveEdit(note) {
      if (!this.editComment.trim()) return;
      this.saveLoading = true;
      this.$http
        .put(`/api/admin/adminNotes/update/${note.id}`, {
          comment: this.editComment,
        })
        .then(({ data }) => {
          const idx = this.rows.findIndex((r) => r.id === note.id);
          if (idx !== -1) this.rows.splice(idx, 1, data.row);
          this.cancelEdit();
          toast('تم تعديل الملاحظة', 'success');
        })
        .catch(() => {
          toast('تعذّر تعديل الملاحظة');
        })
        .finally(() => {
          this.saveLoading = false;
        });
    },

    beforeUnmount() {
      if(this.$swal){
        this.$swal.close();
      }
    },

    async deleteNote(note) {
      const confirm = await this.$swal.fire({
        title:'حذف الملاحظة',
        text:'لا يمكن استعادة الملاحظة بعد الحذف. هل أنت متأكد؟',
        showCancelButton:true,
        confirmButtonText:'حذف',
        cancelButtonText:'إلغاء',
        confirmButtonColor:'#dc3741',
        allowOutsideClick:false,
      });
      if (!confirm.isConfirmed) return;

      this.$http
        .delete(`/api/admin/adminNotes/delete/${note.id}`)
        .then(() => {
          this.countRows = Math.max(0, this.countRows - 1);
          const newTotalPages = Math.ceil(this.countRows / 5);
          if (this.currentPage > newTotalPages && newTotalPages > 0) {
            this.currentPage = newTotalPages;
          }
          this.fetchNotes();
          toast('تم الحذف', 'success');
        })
        .catch(() => {
          toast('تعذّر الحذف');
        });
    },
  },

  created() {
    this.fetchNotes();
  },

  watch: {
    recordId() {
      this.currentPage = 1;
      this.fetchNotes();
    },
  },
};
</script>

<style scoped>
/* Panel shell */
.admin-notes-panel {
  background: #f8faf5;
  border: 1px solid rgba(62, 87, 46, .14);
  border-radius: 12px;
  overflow: hidden;
  margin-top: 20px;
  box-shadow: 0 8px 24px rgba(28, 25, 26, .045);
}

/* Header */
.anp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(90deg, rgba(235, 244, 223, .82), #fff 55%);
  border-bottom: 1px solid rgba(62, 87, 46, .11);
}
.anp-header__title {
  font-size: 14px;
  font-weight: 600;
  color: #26351f;
  display: flex;
  align-items: center;
  gap: 4px;
}
.anp-header__title > i { color: #a69057; }
.anp-badge {
  background: #3e572e;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 20px;
  padding: 1px 7px;
  margin-right: 4px;
}

/* Compose area */
.anp-compose {
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(62, 87, 46, .08);
  background: #fbfcf9;
}
.anp-compose__avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid rgba(166, 144, 87, .24);
  background: #ebf4df;
  color: #3e572e;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex-shrink: 0;
  margin-top: 2px;
}
.anp-compose__field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.anp-textarea {
  width: 100%;
  border: 1px solid #d9ded4;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  resize: vertical;
  outline: none;
  transition: border-color .2s;
  font-family: inherit;
  direction: rtl;
  color: #1c191a;
  background: #fff;
}
.anp-textarea:focus {
  border-color: #a69057;
  box-shadow: 0 0 0 3px rgba(166, 144, 87, .14);
}
.anp-textarea::placeholder { color: #929a8f; }
.anp-textarea--sm {
  font-size: 13px;
}
.anp-compose__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row-reverse;
}
.anp-hint {
  font-size: 10px;
  color: #94a3b8;
}
.anp-submit-btn {
  background: #3e572e;
  color: #fff;
  border: none;
  border-radius: 7px;
  padding: 5px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all .2s;
  display: flex;
  align-items: center;
  gap: 4px;
}
.anp-submit-btn:disabled {
  opacity: .5;
  cursor: not-allowed;
}
.anp-submit-btn:not(:disabled):hover {
  background: #263a20;
  transform: translateY(-1px);
}

/* Body / notes */
.anp-body {
  padding: 8px 16px;
  min-height: 60px;
}
.anp-loader, .anp-empty {
  text-align: center;
  padding: 28px 0;
  color: #94a3b8;
  font-size: 13px;
}

/* Single note */
.anp-note {
  display: flex;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(62, 87, 46, .08);
}
.anp-note:last-child { border-bottom: none; }

.anp-note__avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(166, 144, 87, .20);
  background: #f1f5ec;
  color: #61794c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
  overflow: hidden;
  margin-top: 2px;
}
.anp-note__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.anp-note__body {
  flex: 1;
  min-width: 0;
}
.anp-note__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 3px;
  flex-wrap: wrap;
}
.anp-note__author {
  font-size: 12px;
  font-weight: 600;
  color: #26351f;
}
.anp-note__time {
  font-size: 11px;
  color: #94a3b8;
}
.anp-note__edited {
  font-size: 10px;
  color: #94a3b8;
  font-style: italic;
}
.anp-note__text {
  font-size: 13px;
  color: #343a31;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Note actions */
.anp-note__actions {
  display: flex;
  gap: 8px;
  margin-top: 5px;
  justify-content: flex-end;
}
.anp-action-btn {
  background: none;
  border: none;
  font-size: 11px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  transition: color .15s;
  display: flex;
  align-items: center;
  gap: 3px;
}
.anp-action-btn:hover { color: #3e572e; }
.anp-action-btn--danger:hover { color: #ef4444; }

/* Edit form inside a note */
.anp-note__edit { display: flex; flex-direction: column; gap: 6px; }
.anp-note__edit-actions { display: flex; gap: 8px; }
.anp-btn {
  border: none;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 12px;
  cursor: pointer;
  font-weight: 600;
  transition: all .15s;
}
.anp-btn--save   { background: #3e572e; color: #fff; }
.anp-btn--save:not(:disabled):hover { background: #263a20; }
.anp-btn--cancel { border: 1px solid #d9ded4; background: #f4f7f0; color: #4d5548; }
.anp-btn--cancel:hover { background: #ebf4df; }
.anp-btn:disabled { opacity: .5; cursor: not-allowed; }

/* Pagination */
.anp-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 16px;
  border-top: 1px solid rgba(62, 87, 46, .08);
}
.anp-page-btn {
  background: #f4f7f0;
  border: 1px solid transparent;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all .15s;
  color: #4d5548;
}
.anp-page-btn:hover { border-color: rgba(166, 144, 87, .34); background: #ebf4df; }
.anp-page-btn--active {
  border-color: #3e572e;
  background: #3e572e;
  color: #fff;
  font-weight: 700;
}

/* Transition */
.note-list-enter-active { transition: all .3s ease; }
.note-list-enter-from   { opacity: 0; transform: translateY(-10px); }
.anp-compose__avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.anp-compose__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

</style>

<style>
/* Dark visualIdentity theme (global ancestor selector) */
html[data-bs-theme="dark"] .admin-notes-panel {
  border-color: rgba(166, 144, 87, .24);
  color: #dfe6dc;
  background: #1a2118;
  box-shadow: 0 12px 30px rgba(0, 0, 0, .22);
}
html[data-bs-theme="dark"] .admin-notes-panel .anp-header {
  border-bottom-color: rgba(166, 144, 87, .17);
  background: linear-gradient(90deg, #2b3727, #1c241a 60%);
}
html[data-bs-theme="dark"] .admin-notes-panel .anp-header__title { color: #e2e9de; }
html[data-bs-theme="dark"] .admin-notes-panel .anp-header__title > i { color: #d8ca9f; }
html[data-bs-theme="dark"] .admin-notes-panel .anp-badge {
  border: 1px solid rgba(216, 202, 159, .30);
  color: #171b15;
  background: #b9a363;
}
html[data-bs-theme="dark"] .admin-notes-panel .anp-compose {
  border-bottom-color: rgba(166, 144, 87, .14);
  background: #151b14;
}
html[data-bs-theme="dark"] .admin-notes-panel .anp-compose__avatar,
html[data-bs-theme="dark"] .admin-notes-panel .anp-note__avatar {
  border-color: rgba(216, 202, 159, .25);
  color: #d8ca9f;
  background: #2a3626;
}
html[data-bs-theme="dark"] .admin-notes-panel .anp-textarea {
  border-color: #3b4936;
  color: #e3e9df;
  background: #10150f;
}
html[data-bs-theme="dark"] .admin-notes-panel .anp-textarea::placeholder { color: #778274; }
html[data-bs-theme="dark"] .admin-notes-panel .anp-textarea:focus {
  border-color: #b39b5e;
  box-shadow: 0 0 0 3px rgba(166, 144, 87, .18);
}
html[data-bs-theme="dark"] .admin-notes-panel .anp-submit-btn { background: #4f6a3e; }
html[data-bs-theme="dark"] .admin-notes-panel .anp-submit-btn:not(:disabled):hover { background: #617d50; }
html[data-bs-theme="dark"] .admin-notes-panel .anp-hint,
html[data-bs-theme="dark"] .admin-notes-panel .anp-loader,
html[data-bs-theme="dark"] .admin-notes-panel .anp-empty,
html[data-bs-theme="dark"] .admin-notes-panel .anp-note__time,
html[data-bs-theme="dark"] .admin-notes-panel .anp-note__edited { color: #8f9a8c; }
html[data-bs-theme="dark"] .admin-notes-panel .anp-note { border-bottom-color: rgba(166, 144, 87, .13); }
html[data-bs-theme="dark"] .admin-notes-panel .anp-note__author { color: #b9cdaa; }
html[data-bs-theme="dark"] .admin-notes-panel .anp-note__text { color: #d8e0d4; }
html[data-bs-theme="dark"] .admin-notes-panel .anp-action-btn { color: #9da89a; }
html[data-bs-theme="dark"] .admin-notes-panel .anp-action-btn:hover { color: #d8ca9f; }
html[data-bs-theme="dark"] .admin-notes-panel .anp-action-btn--danger:hover { color: #e4a99b; }
html[data-bs-theme="dark"] .admin-notes-panel .anp-btn--save { background: #4f6a3e; }
html[data-bs-theme="dark"] .admin-notes-panel .anp-btn--save:not(:disabled):hover { background: #617d50; }
html[data-bs-theme="dark"] .admin-notes-panel .anp-btn--cancel {
  border-color: #3b4936;
  color: #c5cec1;
  background: #252e22;
}
html[data-bs-theme="dark"] .admin-notes-panel .anp-btn--cancel:hover { background: #303b2c; }
html[data-bs-theme="dark"] .admin-notes-panel .anp-pagination { border-top-color: rgba(166, 144, 87, .14); }
html[data-bs-theme="dark"] .admin-notes-panel .anp-page-btn {
  border-color: #354132;
  color: #bdc7b9;
  background: #222b20;
}
html[data-bs-theme="dark"] .admin-notes-panel .anp-page-btn:hover {
  border-color: rgba(216, 202, 159, .42);
  color: #eef2eb;
  background: #303b2c;
}
html[data-bs-theme="dark"] .admin-notes-panel .anp-page-btn--active {
  border-color: #a69057;
  color: #fff;
  background: #4f6a3e;
}

</style>
